/**
 * Typisierte Hooks für `/api/v1/due-dates` (Spec 06b, `domains/tasks`) — der
 * Termin, seine Liste über den Objektbaum, die acht Vorlagen und die
 * Intervallauswahl.
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Jede Mutation invalidiert über das Präfix
 * `keys.dueDates.all`, nie einzeln aufgezählt: Anlegen, Ändern, Erledigen,
 * Deaktivieren und Aktivieren können dieselbe Liste zugleich verändern.
 *
 * Es gibt bewusst **keine** `useDueDateSummary` — Spec 06b, „Was aus der
 * vorigen Fassung entfällt": `GET /due-dates/summary` existiert nicht mehr
 * (ADR 0017, kein Bereich, kein Zähl-Endpunkt-Anlass).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type DueDateRow = components['schemas']['DueDateRow']
export type DueDateCreate = components['schemas']['DueDateCreate']
export type DueDateUpdate = components['schemas']['DueDateUpdate']
export type DueDateComplete = components['schemas']['DueDateComplete']
export type DueDateTemplate = components['schemas']['DueDateTemplate']
export type DueDateSubject = components['schemas']['DueDateSubject']
export type DueDateTaskAssignee = components['schemas']['DueDateTaskAssignee']
export type LabeledValue = components['schemas']['LabeledValue']

interface QueryOptions {
  readonly enabled?: boolean
}

export interface DueDatesPage {
  readonly items: DueDateRow[]
  readonly next_cursor: string | null
}

export interface DueDatesFilter {
  readonly scope?: string
  readonly state?: readonly string[]
  readonly limit?: number
  readonly cursor?: string
  readonly updatedSince?: string
}

/**
 * `GET /due-dates` — dieselbe Abfrage für den Reiter *Termine* eines Objekts
 * (`?scope=`, AK-417: Objekt **und** Nachfahren). Gebaut **in** `queryFn` aus
 * einzelnen `filter.*`-Feldern, nicht über eine Hilfsfunktion, die das ganze
 * `filter`-Objekt entgegennimmt — dieselbe Bauform wie `useTicketsList`
 * (`features/tasks/api.ts`), aus demselben Grund
 * (`@tanstack/query/exhaustive-deps` verlangt sonst `filter` unverändert im
 * `queryKey`, obwohl der Key bereits jedes einzelne Feld trägt).
 */
export function useDueDatesList(filter: DueDatesFilter, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.dueDates.list({
      scope: filter.scope,
      state: filter.state,
      limit: filter.limit,
      cursor: filter.cursor,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      for (const state of filter.state ?? []) params.append('state', state)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.cursor !== undefined) params.set('cursor', filter.cursor)
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<DueDatesPage>(`/due-dates${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

/** Cursorfortsetzung für die Termin-Facet eines Objekts. */
export function useInfiniteDueDatesList(filter: DueDatesFilter, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.dueDates.list({
      scope: filter.scope,
      state: filter.state,
      limit: filter.limit,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      for (const state of filter.state ?? []) params.append('state', state)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<DueDatesPage>(`/due-dates${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}

/** `GET /due-dates/templates` — die acht festen Vorlagen (AK-409), ändert
 * sich zur Laufzeit nie, dieselbe Zurückhaltung wie
 * `useTicketStatuses`/`useTicketPriorities`. */
export function useDueDateTemplates() {
  return useQuery({
    queryKey: keys.dueDates.templates,
    queryFn: ({ signal }) => request<DueDateTemplate[]>('/due-dates/templates', { signal }),
    staleTime: Infinity,
  })
}

/** `GET /due-dates/recurrences` — die Intervallstufen (AK-411). Kein Client
 * pflegt eine eigene Zuordnung von Werten auf deutsche Bezeichnungen
 * (`ARCHITECTURE.md` 5). */
export function useDueDateRecurrences() {
  return useQuery({
    queryKey: keys.dueDates.recurrences,
    queryFn: ({ signal }) => request<LabeledValue[]>('/due-dates/recurrences', { signal }),
    staleTime: Infinity,
  })
}

interface CreateDueDateArgs {
  readonly body: DueDateCreate
}

export function useCreateDueDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateDueDateArgs) =>
      request<DueDateRow>('/due-dates', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dueDates.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: derselbe Termin kann im Kalenderfenster stehen — Anlegen,
      // Ändern, Erledigen, Deaktivieren und Aktivieren treffen deshalb
      // zusätzlich dieses Präfix, nie einzeln aufgezählt.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface UpdateDueDateArgs {
  readonly dueDateId: string
  readonly body: DueDateUpdate
  readonly ifMatch: string
}

export function useUpdateDueDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ dueDateId, body, ifMatch }: UpdateDueDateArgs) =>
      request<DueDateRow>(`/due-dates/${dueDateId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dueDates.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: derselbe Termin kann im Kalenderfenster stehen — Anlegen,
      // Ändern, Erledigen, Deaktivieren und Aktivieren treffen deshalb
      // zusätzlich dieses Präfix, nie einzeln aufgezählt.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface CompleteDueDateArgs {
  readonly dueDateId: string
  readonly body: DueDateComplete
  readonly ifMatch: string
}

/** `POST /due-dates/{id}/complete` — eine eigene Aktion, kein `PATCH` auf ein
 * Feld (Spec, „Erledigen ist eine Aktion, kein Feld"): Die Antwort trägt das
 * neue `due_on` bei Wiederholung, kein Client rechnet Monate (AK-412). */
export function useCompleteDueDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ dueDateId, body, ifMatch }: CompleteDueDateArgs) =>
      request<DueDateRow>(`/due-dates/${dueDateId}/complete`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dueDates.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: derselbe Termin kann im Kalenderfenster stehen — Anlegen,
      // Ändern, Erledigen, Deaktivieren und Aktivieren treffen deshalb
      // zusätzlich dieses Präfix, nie einzeln aufgezählt.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface DisableDueDateArgs {
  readonly dueDateId: string
  readonly ifMatch: string
}

export function useDisableDueDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ dueDateId, ifMatch }: DisableDueDateArgs) =>
      request<DueDateRow>(`/due-dates/${dueDateId}/disable`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dueDates.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: derselbe Termin kann im Kalenderfenster stehen — Anlegen,
      // Ändern, Erledigen, Deaktivieren und Aktivieren treffen deshalb
      // zusätzlich dieses Präfix, nie einzeln aufgezählt.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

export function useEnableDueDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ dueDateId, ifMatch }: DisableDueDateArgs) =>
      request<DueDateRow>(`/due-dates/${dueDateId}/enable`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dueDates.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: derselbe Termin kann im Kalenderfenster stehen — Anlegen,
      // Ändern, Erledigen, Deaktivieren und Aktivieren treffen deshalb
      // zusätzlich dieses Präfix, nie einzeln aufgezählt.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}
